import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  CheckCircle as CheckCircle2,
  Envelope as Mail,
  Eye,
  EyeSlash as EyeOff,
  Lock,
  Rocket,
  Shield,
  SpinnerGap as Loader2,
  User,
  WarningCircle as AlertCircle,
  ArrowRight
} from '@phosphor-icons/react';
import { parseApiError } from '@/lib/utils';
import apiClient from '@/lib/axiosInstance';
import { setUserRole } from '@/lib/userRole';
import { storeTokens } from '@/lib/session';

const InitialSetup = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [checkingSetup, setCheckingSetup] = useState(true);

  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await apiClient.get('/auth/setup-status', { timeout: 5000 });
        const data = response.data;
        if (!data.setup_required) {
          const token = localStorage.getItem('access_token');
          const isAuth = token && token.split('.').length === 3 && sessionStorage.getItem('isAuthenticated') === 'true';
          navigate(isAuth ? '/' : '/login', { replace: true });
          return;
        }
        setCheckingSetup(false);
      } catch (err) {
        // silencio-ok: sin respuesta del backend no se ofrece el alta inicial; se redirige al login/panel, que avisan del fallo
        console.error('Error checking setup status:', err.message);
        const token = localStorage.getItem('access_token');
        const isAuth = token && token.split('.').length === 3 && sessionStorage.getItem('isAuthenticated') === 'true';
        navigate(isAuth ? '/' : '/login', { replace: true });
      }
    };

    checkSetupStatus();
  }, [navigate]);

  const validatePassword = (pwd) => {
    const checks = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
    };
    return checks;
  };

  const passwordChecks = validatePassword(password);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (!isPasswordValid) {
      setError('La contraseña no cumple con los requisitos de seguridad');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/auth/initial-setup', {
        email,
        password,
        full_name: fullName
      });

      const data = response.data;

      // Un solo sitio guarda los tokens (FE-11): lib/session.
      storeTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
      localStorage.setItem('user', JSON.stringify(data.user));
      sessionStorage.setItem('isAuthenticated', 'true');
      sessionStorage.setItem('userEmail', data.user.email);
      // Ver `lib/userRole`: el rol va por el almacen compartido, no solo por
      // sessionStorage (que es por pestaña y el token no).
      setUserRole(data.user.role);

      setStep(3);

    } catch (err) {
      setError(parseApiError(err.response?.data?.detail, 'Error en la configuración inicial') || 'Error durante la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/tenant-selection');
  };

  const inputClass =
    'w-full pl-10 pr-4 py-2.5 bg-slate-950/40 border border-white/[0.08] rounded-md text-sm text-white placeholder-slate-600 focus:border-cyan-400/40 focus:outline-none focus:ring-1 focus:ring-cyan-400/20 transition-all';

  if (checkingSetup) {
    return (
      <div className="min-h-screen bg-[#0D1117] flex items-center justify-center font-sans">
        <div className="text-center">
          <div className="w-10 h-10 border border-cyan-500/15 rounded-full animate-spin border-t-cyan-400 mx-auto" />
          <p className="text-slate-500 mt-4 text-xs font-mono uppercase tracking-[0.2em]">Verifying system</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#0D1117] flex items-center justify-center p-6 font-sans antialiased text-slate-100 overflow-hidden">
      {/* Ambient grid */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.45]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(34,211,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.04) 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -left-40 w-[640px] h-[640px] bg-cyan-500/[0.05] rounded-full blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 w-[400px] h-[400px] bg-violet-500/[0.04] rounded-full blur-[120px]"
      />

      {/* Wordmark */}
      <div className="absolute top-6 left-6 flex items-center gap-3 z-10">
        <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.7)]" />
        <span className="text-lg font-semibold text-white tracking-tight">VEKTOR</span>
        <span className="text-[10px] font-mono text-cyan-400/80 uppercase tracking-[0.22em]">CTEM Platform</span>
      </div>

      <Card className="relative z-10 w-full max-w-lg bg-slate-900/40 border border-cyan-500/[0.12] backdrop-blur-xl rounded-xl shadow-[0_24px_64px_-16px_rgba(0,0,0,0.65),0_8px_24px_-8px_rgba(0,0,0,0.4)]">
        {/* Step 1 — Welcome */}
        {step === 1 && (
          <CardContent className="p-7">
            <div className="mb-6">
              <div className="w-11 h-11 rounded-md bg-cyan-500/[0.08] border border-cyan-500/[0.2] flex items-center justify-center mb-5">
                <Shield className="h-5 w-5 text-cyan-400" />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-400/90 mb-2">
                // ONBOARDING
              </p>
              <h2 className="text-2xl font-semibold text-white tracking-tight mb-1">
                Bienvenido a VEKTOR
              </h2>
              <p className="text-slate-500 text-sm">Configuración inicial de la plataforma</p>
            </div>

            <div className="space-y-2.5 mb-6">
              {[
                { icon: CheckCircle2, title: 'Configuración Rápida', desc: 'Crea tu cuenta de administrador en segundos' },
                { icon: Shield, title: 'Seguridad Total', desc: 'Control completo de usuarios, roles y permisos' },
                { icon: Rocket, title: 'Listo para Usar', desc: 'Dashboard, gestión de clientes, y más' }
              ].map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="flex items-start gap-3 p-3 rounded-md bg-slate-950/40 border border-white/[0.06] hover:border-cyan-500/[0.2] transition-colors"
                >
                  <Icon className="h-4 w-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-slate-200 font-medium">{title}</p>
                    <p className="text-xs text-slate-500">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <Button
              type="button"
              onClick={() => setStep(2)}
              className="w-full h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-medium text-sm rounded-md shadow-[0_0_0_1px_rgba(34,211,238,0.3),0_8px_20px_-8px_rgba(34,211,238,0.4)] transition-all"
            >
              <span className="flex items-center justify-center gap-2">
                Comenzar Configuración
                <ArrowRight className="h-4 w-4" />
              </span>
            </Button>

            <p className="mt-4 text-center text-[11px] font-mono uppercase tracking-wider text-slate-600">
              Esta configuración solo se realiza una vez
            </p>
          </CardContent>
        )}

        {/* Step 2 — Admin Account */}
        {step === 2 && (
          <CardContent className="p-7">
            <div className="mb-6">
              <div className="w-11 h-11 rounded-md bg-cyan-500/[0.08] border border-cyan-500/[0.2] flex items-center justify-center mb-5">
                <User className="h-5 w-5 text-cyan-400" />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-400/90 mb-2">
                // ONBOARDING · ADMIN
              </p>
              <h2 className="text-2xl font-semibold text-white tracking-tight mb-1">
                Crear Cuenta de Administrador
              </h2>
              <p className="text-slate-500 text-sm">Esta será tu cuenta principal con acceso total</p>
            </div>

            <form onSubmit={handleSetup} className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                  Nombre Completo
                </label>
                <div className="relative group">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600 group-focus-within:text-cyan-400 transition-colors" />
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Tu nombre completo"
                    className={inputClass}
                    required
                    minLength={2}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                  Email
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600 group-focus-within:text-cyan-400 transition-colors" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@tuempresa.com"
                    className={inputClass}
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                  Contraseña
                </label>
                <div className="relative group">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600 group-focus-within:text-cyan-400 transition-colors" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="w-full pl-10 pr-10 py-2.5 bg-slate-950/40 border border-white/[0.08] rounded-md text-sm text-white placeholder-slate-600 focus:border-cyan-400/40 focus:outline-none focus:ring-1 focus:ring-cyan-400/20 transition-all"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {password && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {[
                      { k: 'length', label: '8+ caracteres' },
                      { k: 'uppercase', label: 'Mayúscula' },
                      { k: 'lowercase', label: 'Minúscula' },
                      { k: 'number', label: 'Número' }
                    ].map(({ k, label }) => (
                      <div
                        key={k}
                        className={`text-[11px] font-mono flex items-center gap-1.5 ${passwordChecks[k] ? 'text-cyan-400' : 'text-slate-600'}`}
                      >
                        <CheckCircle2 className={`h-3.5 w-3.5 ${passwordChecks[k] ? '' : 'opacity-40'}`} />
                        {label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirm */}
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                  Confirmar Contraseña
                </label>
                <div className="relative group">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600 group-focus-within:text-cyan-400 transition-colors" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className={`w-full pl-10 pr-4 py-2.5 bg-slate-950/40 border rounded-md text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 transition-all ${
                      confirmPassword && confirmPassword !== password
                        ? 'border-red-500/[0.4] focus:border-red-500/[0.5] focus:ring-red-500/20'
                        : 'border-white/[0.08] focus:border-cyan-400/40 focus:ring-cyan-400/20'
                    }`}
                    required
                  />
                </div>
                {confirmPassword && confirmPassword !== password && (
                  <p className="text-[11px] text-red-400 mt-1.5 font-mono">Las contraseñas no coinciden</p>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/[0.06] border border-red-500/[0.25] rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <span className="text-xs text-red-300">{error}</span>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 border-white/[0.08] hover:border-cyan-500/[0.2] hover:bg-cyan-500/[0.04] text-slate-200"
                >
                  Atrás
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !isPasswordValid || password !== confirmPassword}
                  className="flex-1 h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-medium text-sm rounded-md shadow-[0_0_0_1px_rgba(34,211,238,0.3),0_8px_20px_-8px_rgba(34,211,238,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Configurando
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Crear Cuenta
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        )}

        {/* Step 3 — Success */}
        {step === 3 && (
          <CardContent className="p-7">
            <div className="mb-6">
              <div className="w-11 h-11 rounded-md bg-emerald-500/[0.08] border border-emerald-500/[0.25] flex items-center justify-center mb-5">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-400/90 mb-2">
                // ONBOARDING · COMPLETE
              </p>
              <h2 className="text-2xl font-semibold text-white tracking-tight mb-1">
                ¡Configuración Completada!
              </h2>
              <p className="text-slate-500 text-sm">Tu cuenta de administrador ha sido creada</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="p-5 rounded-md bg-slate-950/40 border border-cyan-500/[0.15]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-md bg-cyan-500/[0.08] border border-cyan-500/[0.2] flex items-center justify-center">
                    <User className="h-4 w-4 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-slate-200 text-sm font-medium">{fullName}</p>
                    <p className="text-xs text-slate-500 font-mono">{email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-cyan-500/[0.08] border border-cyan-500/[0.25] text-cyan-300">
                    Administrador
                  </span>
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-violet-500/[0.08] border border-violet-500/[0.25] text-violet-300">
                    Acceso Total
                  </span>
                </div>
              </div>

              <div className="text-xs text-slate-400 space-y-2">
                {[
                  'Gestiona usuarios y roles desde el panel de control',
                  'Configura clientes y sus permisos',
                  'Accede a todas las funciones de seguridad'
                ].map((item) => (
                  <p key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-cyan-400 flex-shrink-0" />
                    {item}
                  </p>
                ))}
              </div>
            </div>

            <Button
              type="button"
              onClick={handleContinue}
              className="w-full h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-medium text-sm rounded-md shadow-[0_0_0_1px_rgba(34,211,238,0.3),0_8px_20px_-8px_rgba(34,211,238,0.4)] transition-all"
            >
              <span className="flex items-center justify-center gap-2">
                <Shield className="h-4 w-4" />
                Ir al Dashboard
                <ArrowRight className="h-4 w-4" />
              </span>
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default InitialSetup;
